/**
 * src/hooks/useMyWorkflowSteps.js
 *
 * The open human workflow steps the signed-in person carries (PDWB-02;
 * docs/workflow-engine-design.md §3b.2, §3f) — the `workflow-step` source of
 * the one task list (collectTaskSources). One store per (tenant, person),
 * shared by the ◎ badge, the Action Centre, the Tasks page and the calendar,
 * so every surface counts a step once, the same way (TASKS-B-001, WF-B-002).
 *
 * The server (workflow-runs-read ?view=open-steps) returns only the caller's
 * own steps, plus — for a tenant admin — the unassigned ones every admin
 * carries. Nothing here is a copy of a step's status: each refresh replaces
 * the list, and a step completed anywhere disappears on the next read.
 *
 * A 403 (a plan without workflows, D8 = B) reads as "no workflow steps" —
 * though by WF-B-010 the server keeps serving a downgraded tenant's open
 * steps, so this is only the fallback. Any OTHER failure keeps the last list
 * read and marks the store failed (useMyWorkflowStepsFailed), so a surface can
 * say the steps could not be loaded — a failed read never silently empties
 * someone's work (PDWB-02 co-owner). The rest of the Action Centre never
 * waits for it.
 */

import { useEffect, useSyncExternalStore } from 'react';
import {
  listMyOpenWorkflowSteps,
  listTenantOpenWorkflowSteps,
  listMyHaltReviews,
  listTenantHaltReviews,
} from '../api/storage-azure.js';

const EMPTY = [];
const stores = new Map();

function getStore(key, read = listMyOpenWorkflowSteps) {
  let store = stores.get(key);
  if (!store) {
    store = {
      key,
      read,
      steps: EMPTY,
      // unreadable / truncated as the server reported them (GH #7583), so a
      // surface never takes a partial list as whole.
      partial: false,
      failed: false,
      listeners: new Set(),
      loaded: false,
      inflight: null,
    };
    stores.set(key, store);
  }
  return store;
}

function refresh(store) {
  if (store.inflight) return store.inflight;
  store.inflight = Promise.resolve()
    .then(() => store.read())
    .then((res) => {
      store.steps = Array.isArray(res?.steps) ? res.steps : EMPTY;
      store.partial = Boolean(res?.truncated) || Number(res?.unreadable) > 0;
      store.failed = false;
    })
    .catch((e) => {
      if (e?.httpStatus === 403) {
        // The plan gate: this tenant has no workflow steps to show.
        store.steps = EMPTY;
        store.failed = false;
      } else {
        // Keep what was last read; say the read failed.
        store.failed = true;
      }
    })
    .finally(() => {
      store.loaded = true;
      store.inflight = null;
      store.view = null;
      for (const l of store.listeners) l();
    });
  return store.inflight;
}

/** Re-read every held list — after a step is completed, reassigned or a run started / cancelled. */
export function refreshMyWorkflowSteps() {
  return Promise.all([...stores.values()].map(refresh));
}

/**
 * @param {object} session  the viewer's session
 * @param {object|string} tenant  the current tenant (or its id); nothing is fetched without one
 * @param {{ refreshOnMount?: boolean }} [opts]
 * @returns {object[]} open step instances, each with its runId
 */
export function useMyWorkflowSteps(session, tenant, { refreshOnMount = false } = {}) {
  const tenantId = (typeof tenant === 'string' ? tenant : tenant?.id) || session?.tenantId;
  const userId = session?.userId;
  // A super-admin's session is not a tenant person: the endpoint refuses it.
  const enabled = Boolean(tenantId && userId && session?.role !== 'super-admin');
  const store = enabled ? getStore(`${tenantId}::${userId}`) : null;

  const steps = useSyncExternalStore(
    (cb) => {
      if (!store) return () => {};
      store.listeners.add(cb);
      return () => store.listeners.delete(cb);
    },
    () => (store ? store.steps : EMPTY)
  );

  useEffect(() => {
    if (!store) return;
    if (refreshOnMount || !store.loaded) refresh(store);
  }, [store, refreshOnMount]);

  return steps;
}

/**
 * True while the last read of the viewer's workflow steps failed (anything
 * but the plan's 403) — the list shown is then the last one read, or none.
 */
export function useMyWorkflowStepsFailed(session, tenant) {
  const tenantId = (typeof tenant === 'string' ? tenant : tenant?.id) || session?.tenantId;
  const userId = session?.userId;
  const enabled = Boolean(tenantId && userId && session?.role !== 'super-admin');
  const store = enabled ? getStore(`${tenantId}::${userId}`) : null;
  return useSyncExternalStore(
    (cb) => {
      if (!store) return () => {};
      store.listeners.add(cb);
      return () => store.listeners.delete(cb);
    },
    () => Boolean(store && store.failed)
  );
}

// ── The tenant's open work (GH #7583) ──────────────────────────────────────
//
// The tenant-wide figures — the dashboard's overdue / due-soon counts, the
// Tasks page and the calendar a dashboard tile drills into — count every
// person's open step, not only the viewer's. Only an admin or manager may read
// another person's step (WF-B-003), so only they get the tenant view; anyone
// else gets their own steps and `scope: 'own'`, which a tenant-wide figure must
// never present as the tenant's total.

const TENANT_READ_ROLES = new Set(['admin', 'manager']);

// A stable view per scope while nothing is read (no tenant, a super-admin):
// useSyncExternalStore needs the same object back on every call.
const NO_STORE_VIEWS = {
  tenant: { steps: EMPTY, scope: 'tenant', partial: false, failed: false, loaded: false },
  own: { steps: EMPTY, scope: 'own', partial: false, failed: false, loaded: false },
};

function viewOf(store, scope) {
  if (!store) return NO_STORE_VIEWS[scope];
  if (!store.view) {
    store.view = {
      steps: store.steps,
      scope,
      partial: store.partial,
      failed: store.failed,
      loaded: store.loaded,
    };
  }
  return store.view;
}

/**
 * The workflow steps that belong in the tenant's open work, as far as this
 * viewer may read them.
 *
 * @returns {{ steps: object[], scope: 'tenant'|'own', partial: boolean, failed: boolean, loaded: boolean }}
 *   scope 'tenant' — every person's open step; 'own' — the viewer's only.
 */
export function useOpenWorkflowSteps(session, tenant) {
  const tenantId = (typeof tenant === 'string' ? tenant : tenant?.id) || session?.tenantId;
  const userId = session?.userId;
  const enabled = Boolean(tenantId && userId && session?.role !== 'super-admin');
  const tenantWide = enabled && TENANT_READ_ROLES.has(session?.role);
  const scope = tenantWide ? 'tenant' : 'own';
  const store = !enabled
    ? null
    : tenantWide
      ? getStore(`${tenantId}::*tenant*`, () => listTenantOpenWorkflowSteps(tenantId))
      : getStore(`${tenantId}::${userId}`);

  const view = useSyncExternalStore(
    (cb) => {
      if (!store) return () => {};
      store.listeners.add(cb);
      return () => store.listeners.delete(cb);
    },
    () => viewOf(store, scope)
  );

  useEffect(() => {
    if (store && !store.loaded) refresh(store);
  }, [store]);

  return view;
}

/** Test seam. */
export function __resetWorkflowStepStores() {
  stores.clear();
}

// ── Reviews of halted recurring workflows (PDW-HALT-2) ──────────────────────
//
// The `workflow-halt-review` source of the one task list
// (docs/workflow-halt-review-design.md §4): the same store semantics as the
// steps above — one store per (tenant, person), shared by the badge, the
// Action Centre, the Tasks page and the calendar, so a review is counted once.
// A 403 reads as "no reviews"; any other failure keeps the last list read and
// marks the store failed, so a failed read never silently empties someone's
// work. The read checks no plan gate and no release flag on the server
// (RFLG-B-006): it is never hidden by the flag that caused the halt.
//
// Every OPEN review is owed work, whatever its cause (WF-B-011, slice 4b). The
// tenant view also carries each definition's latest closed review (management
// review needs them), so only open ones are kept here. A review whose own
// decision opened a re-enable request is closed: decided, not owed.

const haltStores = new Map();

/** Owed work: an open review. */
const isOwedReview = (r) => !!r && (r.status === 'pending' || r.status === 'in-progress');

function getHaltStore(key, read = listMyHaltReviews) {
  let store = haltStores.get(key);
  if (!store) {
    store = {
      key,
      read,
      reviews: EMPTY,
      partial: false,
      failed: false,
      listeners: new Set(),
      loaded: false,
      inflight: null,
      view: null,
    };
    haltStores.set(key, store);
  }
  return store;
}

function refreshHalt(store) {
  if (store.inflight) return store.inflight;
  store.inflight = Promise.resolve()
    .then(() => store.read())
    .then((res) => {
      store.reviews = Array.isArray(res?.reviews) ? res.reviews.filter(isOwedReview) : EMPTY;
      store.partial = Boolean(res?.truncated);
      store.failed = false;
    })
    .catch((e) => {
      if (e?.httpStatus === 403) {
        store.reviews = EMPTY;
        store.failed = false;
      } else {
        store.failed = true;
      }
    })
    .finally(() => {
      store.loaded = true;
      store.inflight = null;
      store.view = null;
      for (const l of store.listeners) l();
    });
  return store.inflight;
}

/** Re-read every held review list — after a review is decided. */
export function refreshMyHaltReviews() {
  return Promise.all([...haltStores.values()].map(refreshHalt));
}

function personalHaltStore(session, tenant) {
  const tenantId = (typeof tenant === 'string' ? tenant : tenant?.id) || session?.tenantId;
  const userId = session?.userId;
  const enabled = Boolean(tenantId && userId && session?.role !== 'super-admin');
  return enabled ? getHaltStore(`${tenantId}::${userId}`) : null;
}

function subscribe(store) {
  return (cb) => {
    if (!store) return () => {};
    store.listeners.add(cb);
    return () => store.listeners.delete(cb);
  };
}

/**
 * The open halt reviews the viewer carries (own, plus unassigned for an admin).
 * @returns {object[]} review rows
 */
export function useMyHaltReviews(session, tenant, { refreshOnMount = false } = {}) {
  const store = personalHaltStore(session, tenant);
  const reviews = useSyncExternalStore(subscribe(store), () => (store ? store.reviews : EMPTY));
  useEffect(() => {
    if (!store) return;
    if (refreshOnMount || !store.loaded) refreshHalt(store);
  }, [store, refreshOnMount]);
  return reviews;
}

/** True while the last read of the viewer's halt reviews failed (anything but a 403). */
export function useMyHaltReviewsFailed(session, tenant) {
  const store = personalHaltStore(session, tenant);
  return useSyncExternalStore(subscribe(store), () => Boolean(store && store.failed));
}

const NO_HALT_VIEWS = {
  tenant: { reviews: EMPTY, scope: 'tenant', partial: false, failed: false, loaded: false },
  own: { reviews: EMPTY, scope: 'own', partial: false, failed: false, loaded: false },
};

function haltViewOf(store, scope) {
  if (!store) return NO_HALT_VIEWS[scope];
  if (!store.view) {
    store.view = {
      reviews: store.reviews,
      scope,
      partial: store.partial,
      failed: store.failed,
      loaded: store.loaded,
    };
  }
  return store.view;
}

/**
 * The halt reviews that belong in the tenant's open work, as far as this
 * viewer may read them — every person's for an admin or manager, else the
 * viewer's own (`scope: 'own'`), exactly like useOpenWorkflowSteps.
 *
 * @returns {{ reviews: object[], scope: 'tenant'|'own', partial: boolean, failed: boolean, loaded: boolean }}
 */
export function useOpenHaltReviews(session, tenant) {
  const tenantId = (typeof tenant === 'string' ? tenant : tenant?.id) || session?.tenantId;
  const userId = session?.userId;
  const enabled = Boolean(tenantId && userId && session?.role !== 'super-admin');
  const tenantWide = enabled && TENANT_READ_ROLES.has(session?.role);
  const scope = tenantWide ? 'tenant' : 'own';
  const store = !enabled
    ? null
    : tenantWide
      ? getHaltStore(`${tenantId}::*tenant*`, () => listTenantHaltReviews(tenantId))
      : getHaltStore(`${tenantId}::${userId}`);
  const view = useSyncExternalStore(subscribe(store), () => haltViewOf(store, scope));
  useEffect(() => {
    if (store && !store.loaded) refreshHalt(store);
  }, [store]);
  return view;
}

/** Test seam. */
export function __resetHaltReviewStores() {
  haltStores.clear();
}
